import React from 'react';

/**
 * Common responsive styles for report components
 * These are component wrappers with responsive Tailwind CSS classes
 */

interface ResponsiveContainerProps {
  children: React.ReactNode;
  className?: string;
}

// Main container for report pages
export const ReportPageContainer: React.FC<ResponsiveContainerProps> = ({ children, className = '' }) => (
  <div className={`bg-white p-2 sm:p-4 md:p-6 font-sans ${className}`}>
    {children}
  </div>
);

// Container for report headers with responsive flex layout
export const ReportHeaderContainer: React.FC<ResponsiveContainerProps> = ({ children, className = '' }) => (
  <div className={`flex flex-col lg:flex-row justify-between items-center mb-4 md:mb-6 ${className}`}>
    {children}
  </div>
);

// Container for filter panels that collapse on mobile
export const FilterPanelContainer: React.FC<ResponsiveContainerProps> = ({ children, className = '' }) => (
  <div className={`bg-white shadow rounded-lg mb-4 md:mb-6 ${className}`}>
    {children}
  </div>
);

// Grid container for KPI cards with responsive columns
export const KPICardGrid: React.FC<ResponsiveContainerProps> = ({ children, className = '' }) => (
  <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 md:gap-4 ${className}`}>
    {children}
  </div>
);

// Container for chart sections with responsive height
export const ChartContainer: React.FC<ResponsiveContainerProps> = ({ children, className = '' }) => (
  <div className={`h-64 sm:h-72 md:h-80 lg:h-96 ${className}`}>
    {children}
  </div>
);

// Report section container with proper spacing
export const ReportSection: React.FC<ResponsiveContainerProps> = ({ children, className = '' }) => (
  <div className={`mb-6 md:mb-12 ${className}`}>
    {children}
  </div>
);

// Table container with horizontal scroll on mobile
export const ResponsiveTableContainer: React.FC<ResponsiveContainerProps> = ({ children, className = '' }) => (
  <div className={`overflow-x-auto mt-4 md:mt-6 ${className}`}>
    {children}
  </div>
);

// Responsive heading text styles
export const ReportHeading: React.FC<ResponsiveContainerProps> = ({ children, className = '' }) => (
  <h2 className={`text-lg sm:text-xl md:text-2xl font-bold mb-2 md:mb-4 text-gray-800 ${className}`}>
    {children}
  </h2>
);

// Responsive subheading text styles
export const ReportSubheading: React.FC<ResponsiveContainerProps> = ({ children, className = '' }) => (
  <h3 className={`text-base sm:text-lg md:text-xl font-semibold mb-2 md:mb-3 text-gray-800 ${className}`}>
    {children}
  </h3>
);

// Insights panel with responsive padding
export const InsightsPanel: React.FC<ResponsiveContainerProps> = ({ children, className = '' }) => (
  <div className={`p-3 sm:p-4 md:p-6 bg-gray-50 rounded-lg ${className}`}>
    {children}
  </div>
);

// Modal container with responsive width
export const ResponsiveModal: React.FC<ResponsiveContainerProps> = ({ children, className = '' }) => (
  <div className={`bg-white rounded-lg p-4 md:p-6 w-full max-w-xs sm:max-w-sm md:max-w-md lg:max-w-lg ${className}`}>
    {children}
  </div>
);

// Button group with proper spacing on mobile
export const ButtonGroup: React.FC<ResponsiveContainerProps> = ({ children, className = '' }) => (
  <div className={`flex flex-wrap gap-2 ${className}`}>
    {children}
  </div>
);

export default {
  ReportPageContainer,
  ReportHeaderContainer,
  FilterPanelContainer,
  KPICardGrid,
  ChartContainer,
  ReportSection,
  ResponsiveTableContainer,
  ReportHeading,
  ReportSubheading,
  InsightsPanel,
  ResponsiveModal,
  ButtonGroup
};